import { useForm } from '@/components/forms';
import { nameValidationRegex } from '@/constants/regex';
import { useAttributes } from '@/hooks/useAttributes';
import { useDropdown } from '@/hooks/useDropdown';
import { isGoogleMapsUrl } from '@/lib/isGoogleMapsUrl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';
import { storeInfoSchema } from '../../StoreInfo';

type Option = {
  value: string;
  label: string;
  disable?: boolean;
  /** fixed option that can't be removed. */
  fixed?: boolean;
  /** Group the options by providing key. */
  [key: string]: string | boolean | undefined;
};
const instagramUrlRegex = /^(https?:\/\/)?(www\.)?instagram\.com\/.+$/i;

const tiktokUrlRegex = /^(https?:\/\/)?(www\.)?tiktok\.com\/.+$/i;

const lineUrlRegex = /^(https?:\/\/)?(www\.)?(line\.me|lin\.ee)\/.+$/i;

const twitterUrlRegex = /^(https?:\/\/)?(www\.)?(twitter\.com|x\.com)\/.+$/i;

const websiteUrlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i;

export const addFormSchema = z.object({
  ...storeInfoSchema.shape,
  postalCode: z
    .string()
    .trim()
    .min(1, 'common.form.validation.required')
    .regex(/^\d{3}-?\d{4}$/, 'common.form.validation.invalidPostalCode'),
  prefecture: z.string().refine((value) => value !== '' && value !== 'default', {
    message: 'common.form.validation.required',
  }),
  city: z
    .string()
    .trim()
    .min(1, 'common.form.validation.required')
    .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
  street: z
    .string()
    .trim()
    .min(1, 'common.form.validation.required')
    .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
  buildingNo: z
    .string()
    .trim()
    .regex(nameValidationRegex, 'common.form.validation.nameOnly')
    .optional()
    .or(z.literal('')),
  workingTimeFrom: z.string().refine((value) => value !== '' && value !== 'default', {
    message: 'common.form.validation.required',
  }),
  workingTimeTo: z.string().refine((value) => value !== '' && value !== 'default', {
    message: 'common.form.validation.required',
  }),
  lastEntryTime: z.string().refine((value) => value !== '' && value !== 'default', {
    message: 'common.form.validation.required',
  }),
  lastOrderTime: z.string().trim().optional(),
  holidays: z.string().trim().optional(),
  otherHolidays: z.array(z.string()).optional().default([]),
  tax: z.string().trim().optional(),
  closingDate: z.string().trim().optional(),
  instagram: z.string().regex(instagramUrlRegex, 'common.form.validation.enterValidUrl').optional().or(z.literal('')),
  tiktok: z.string().regex(tiktokUrlRegex, 'common.form.validation.enterValidUrl').optional().or(z.literal('')),
  line: z.string().regex(lineUrlRegex, 'common.form.validation.enterValidUrl').optional().or(z.literal('')),
  twitter: z.string().regex(twitterUrlRegex, 'common.form.validation.enterValidUrl').optional().or(z.literal('')),
  website: z.string().regex(websiteUrlRegex, 'common.form.validation.enterValidUrl').optional().or(z.literal('')),
  googleMap: z
    .string()
    .trim()
    .min(1, 'common.form.validation.required')
    .refine(isGoogleMapsUrl, 'common.form.validation.enterValidUrl'),
});

export const useUpdateStoreFunction = () => {
  const [profileImage, setProfileImage] = useState('');
  const [coverImage, setCoverImage] = useState('');

  // Fetch data
  const { data: prefectureOptionsRaw = [] } = useDropdown('Location') || {};
  const { data: holidaysOptionsRaw = [] } = useAttributes('holidays');
  const { data: otherHolidays = [] } = useAttributes('other_holidays');

  // Memoize options to prevent unnecessary rerenders
  const prefectureOptions = useMemo(() => prefectureOptionsRaw, [prefectureOptionsRaw]);
  const holidaysOptions = useMemo(() => holidaysOptionsRaw, [holidaysOptionsRaw]);

  const otherHolidaysOptions: Option[] = useMemo(() => {
    return (otherHolidays ?? []).map((item) => ({
      value: String(item.value),
      label: item.label,
      labelEng: item.labelEng ?? item.label,
    }));
  }, [otherHolidays]);

  // Initialize form once
  const methods = useForm({
    schema: addFormSchema,
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      storeName: '',
      storeNameFurigana: '',
      groupName: '',
      landlineNumber: '',
      description: '',
      postalCode: '',
      prefecture: '',
      city: '',
      street: '',
      buildingNo: '',
      workingTimeFrom: '',
      workingTimeTo: '',
      lastEntryTime: '',
      lastOrderTime: '',
      holidays: '',
      otherHolidays: [],
      tax: '',
      closingDate: '',
      instagram: '',
      tiktok: '',
      line: '',
      twitter: '',
      website: '',
      googleMap: '',
    },
  });

  const options = useMemo(() => {
    const hours = [...Array(24).keys()].map((hour) => hour.toString().padStart(2, '0'));
    const minutes = ['00', '15', '30', '45'];
    return hours.flatMap((hour) =>
      minutes.map((minute) => ({ value: `${hour}:${minute}`, label: `${hour}:${minute}` })),
    );
  }, []);

  const workingFrom = useWatch({
    control: methods.control,
    name: 'workingTimeFrom',
  });
  const workingTo = useWatch({
    control: methods.control,
    name: 'workingTimeTo',
  });

  const toMinutes = useCallback((time?: string) => {
    if (!time) return null;
    const [h, m] = time.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  }, []);

  const lastEntryOptions = useMemo(() => {
    const fromMin = toMinutes(workingFrom);
    const toMin = toMinutes(workingTo);

    if (fromMin === null || toMin === null) {
      return [...options];
    }

    if (toMin > fromMin) {
      const maxMin = toMin - 60;
      if (maxMin < fromMin) return [];

      return [
        ...options.filter((opt) => {
          const m = toMinutes(opt.value)!;
          return m >= fromMin && m <= maxMin;
        }),
      ];
    }

    return [
      ...options.filter((opt) => {
        const m = toMinutes(opt.value)!;
        return m >= fromMin;
      }),
    ];
  }, [workingFrom, workingTo, options, toMinutes]);

  useEffect(() => {
    if (!workingTo) return;

    const last = lastEntryOptions.at(-1);
    if (!last?.value) return;

    methods.setValue('lastEntryTime', last.value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [workingTo, lastEntryOptions, methods]);

  // Memoize the return object
  return useMemo(
    () => ({
      methods,
      options,
      lastEntryOptions,
      prefectureOptions,
      holidaysOptions,
      otherHolidaysOptions,
      profileImage,
      coverImage,
      setProfileImage,
      setCoverImage,
    }),
    [
      methods,
      options,
      lastEntryOptions,
      prefectureOptions,
      holidaysOptions,
      otherHolidaysOptions,
      profileImage,
      coverImage,
    ],
  ); // setProfileImage and setCoverImage are stable from useState
};
